'use client';

import { faArrowRight, faChevronDown, faDesktop } from '@fortawesome/pro-light-svg-icons';
import { useState } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Icon } from '@/components/ui/icon.component';
import { SearchPartnerData } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { addressFormatter, formatGoogleMapsAddress } from '@/utils/formatters/format-address';
import { formatDistance } from '@/utils/formatters/format-distance';
import { Link } from '@/components/ui/link/link.component';
import { googleMapsBaseUrl } from '@/utils/consts/google-maps.consts';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface SearchPartnerAddressProps {
  partner: SearchPartnerData['partner'];
  telehealth: boolean;
  isPreciseLocation: boolean;
  distance: number;
}

export const SearchPartnerAddress = ({
  partner,
  telehealth,
  isPreciseLocation,
  distance,
}: SearchPartnerAddressProps) => {
  const [expanded, setExpanded] = useState(false);
  const analytics = getAnalyticsPlugin();

  const toggleExpand = () => {
    if (!expanded) {
      analytics.track(AnalyticsEnum.CLICK_EXPAND_SEARCH_RESULT_ADDRESS, { partner_id: partner.id });
    }
    setExpanded((prev) => !prev);
  };

  const trackOpenMapClick = () => {
    analytics.track(AnalyticsEnum.CLICK_OPEN_MAP_SEARCH_RESULT, { partner_id: partner.id });
  };

  const viewInMap = () => {
    const addressToSearch = formatGoogleMapsAddress(partner.facility);
    const url = `${googleMapsBaseUrl}${addressToSearch}`;
    return url;
  };

  return (
    <>
      {!telehealth
        ? (
            <>
              <div className="flex items-center gap-2 mt-2 w-full">
                {isPreciseLocation
                  ? (
                      <div className="inline-flex text-nowrap flex-1 overflow-hidden text-ellipsis">
                        <Typography variant={TypographyVariant.BODY_TEXT} className="font-semibold">
                          {formatDistance(distance)}
                          {' '}
                          km
                        </Typography>
                        <Typography variant={TypographyVariant.BODY_TEXT}>
                          &nbsp;
                          •
                          &nbsp;
                          {partner.facility.neighborhood}
                        </Typography>
                      </div>
                    )
                  : (
                      <div className="flex flex-1 overflow-hidden">
                        <Typography variant={TypographyVariant.BODY_TEXT} className="truncate">
                          {partner.facility.neighborhood}
                          ,
                          {' '}
                          {partner.facility.city}
                          {' '}
                          -
                          {' '}
                          {partner.facility.state}
                        </Typography>
                      </div>
                    )}
                <div
                  onClick={toggleExpand}
                  className="transition-colors duration-300 ease-in-out text-center bg-gray-200 hover:bg-gray-300 w-6 h-6 rounded-full content-center hover:cursor-pointer flex-shrink-0 ml-auto"
                  data-testid={DataTestIdEnum.SEARCH_CARD_PARTNER_ADDRESS_EXPAND_BUTTON}
                >
                  <Icon
                    icon={faChevronDown}
                    className={`transition-transform duration-300 ease-in-out text-gray-700 ${
                      expanded ? 'rotate-180' : ''
                    }`}
                  />
                </div>
              </div>
              <div
                className={`transition-all duration-500 ease-in-out ${expanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}
              >
                <div className="mt-2">
                  <Typography
                    variant={TypographyVariant.BODY_TEXT}
                    className="text-gray-700 break-words"
                    data-testid={DataTestIdEnum.SEARCH_CARD_PARTNER_ADDRESS_FULL_ADDRESS_LINK}
                  >
                    {addressFormatter(partner.facility, 'fullWithoutZipcode')}
                  </Typography>
                </div>

                <Link
                  prefetch={false}
                  href={viewInMap()}
                  className="inline-flex items-center gap-2 py-2 font-semibold text-green-500 hover:underline"
                  target="_blank"
                  rel="noreferrer"
                  data-testid={DataTestIdEnum.SEARCH_CARD_PARTNER_ADDRESS_MAP_LINK}
                  onClick={trackOpenMapClick}
                >
                  Abrir no mapa
                  <Icon icon={faArrowRight} className="text-green-500" />
                </Link>
              </div>
            </>
          )
        : (
            <div className="flex mt-4 items-center" data-testid={DataTestIdEnum.SEARCH_CARD_PARTNER_ADDRESS_TELEHEALTH_INDICATOR}>
              <Icon icon={faDesktop} className="text-gray-700 w-5 h-5" size="lg" />
              <Typography variant={TypographyVariant.BODY_TEXT} className="text-gray-700 ml-2">
                Teleconsulta
              </Typography>
            </div>
          )}
    </>
  );
};
